import { Button } from "@gigpro/ui/Button";
import { DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { useNow } from "@gigpro/ui/utils/useNow";
import { useLocationsRetrieve } from "@sidegigllc/api/client";
import { JobsTrialStepContainer } from "domains/hire/containers/JobsTrialStep";
import { useJobsGateState } from "domains/hire/hooks/useJobsGateState";
import { type JobsGate, askAnAdmin, jobsTeaserCopy } from "domains/hire/jobsPlan";
import * as React from "react";

type JobsGateStepProps = {
  gate: JobsGate;
  locationId: number;
  /** What to do once the plan is running, e.g. send the offer that was waiting. */
  onStarted: () => Promise<unknown>;
  onBack: () => void;
};

/** Body for a dialog whose Continue is blocked by the Jobs gate: the trial or resubscribe step, or "ask an admin". */
const JobsGateStep = ({ gate, locationId, onStarted, onBack }: JobsGateStepProps) => {
  const { location, status } = useJobsGateState(locationId);
  const now = useNow({ updateIntervalMs: false });
  if (gate !== "askAdmin") {
    return (
      <>
        <DialogTitle className="sr-only">{gate === "trial" ? "Start your free trial" : "Resubscribe"}</DialogTitle>
        <JobsTrialStepContainer
          locationId={locationId}
          submitLabel="Start free trial"
          saveSubmitLabel="Save and start free trial"
          purpose="unlock"
          onStarted={onStarted}
          onBack={onBack}
        />
      </>
    );
  }
  const teaser = jobsTeaserCopy(status, { locationName: location?.name ?? "", trialEndsAt: now });
  const copy = teaser && askAnAdmin(teaser, location?.name ?? "your location");
  return (
    <>
      <DialogTitle>{copy?.title}</DialogTitle>
      <p className="text-normal text-black">{copy?.body}</p>
      <DialogFooter>
        <Button variant="contained" className="w-full" onClick={onBack}>
          Back
        </Button>
      </DialogFooter>
    </>
  );
};

/**
 * Runs `action` straight away unless the Jobs gate blocks it. A blocked action waits behind `step`, which the caller
 * renders in place of its own content, and runs once the trial or resubscribe succeeds. `ready` is false while the
 * location's plan is still loading; if that request fails there is no gate to show, so the action goes ahead and the
 * API stays the final check.
 */
export const useJobsGatedAction = (locationId: number) => {
  const { gate } = useJobsGateState(locationId);
  const { isPending } = useLocationsRetrieve(locationId);
  // The gate is captured when blocked: starting the plan clears it, and the step must stay up until it finishes.
  // oxlint-disable-next-line typescript/no-invalid-void-type -- callback may return a promise or nothing
  const [blocked, setBlocked] = React.useState<{ gate: JobsGate; action: () => Promise<unknown> | void } | null>(null);

  // oxlint-disable-next-line typescript/no-invalid-void-type -- callback may return a promise or nothing
  const run = (action: () => Promise<unknown> | void) => {
    if (!gate) return action();
    setBlocked({ gate, action });
  };

  const step = blocked && (
    <JobsGateStep
      gate={blocked.gate}
      locationId={locationId}
      onStarted={async () => {
        setBlocked(null);
        try {
          await blocked.action();
        } catch {
          // The action reports its own failure, as it does when it runs ungated.
        }
      }}
      onBack={() => setBlocked(null)}
    />
  );

  // Until the request settles the gate can't tell, so the caller holds its Continue rather than let the action slip by
  return { run, step, ready: !isPending };
};
